/**
 * RevenueCat native billing service for the Capacitor (iOS / Android)
 * builds.
 *
 * The web + desktop clients keep their existing Stripe / Steam flows
 * (see `billing-client.ts` and `upgrade-modal.ts`); this module is the
 * single place the native shells talk to StoreKit / Google Play
 * Billing through the RevenueCat SDK.
 *
 * Product / entitlement mapping (configured in the RevenueCat
 * dashboard, NOT in code):
 *
 *   entitlement  "Zerion Online Pro"  ← what gameplay code checks
 *   products     "monthly", "yearly"  ← attached to the entitlement
 *   offering     current              ← packages shown on the paywall
 *
 * Every export is a no-op / null outside the Capacitor native shells,
 * so callers never need their own platform guards beyond reading the
 * return values. All SDK failures are caught and logged here — billing
 * must never crash the game loop.
 */
import {
  Purchases,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
  type CustomerInfo,
  type PurchasesEntitlementInfo,
  type PurchasesOffering,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, PAYWALL_RESULT } from '@revenuecat/purchases-capacitor-ui';
import { isCapacitorNative, isCapacitorIOS } from '../input/mobile';

/** Entitlement identifier configured in the RevenueCat dashboard. */
export const PRO_ENTITLEMENT_ID = 'Zerion Online Pro';

/** Store product identifiers attached to the Pro entitlement. */
export const PRO_PRODUCT_IDS = {
  monthly: 'monthly',
  yearly: 'yearly',
} as const;

/**
 * Snapshot of the player's Pro entitlement, reduced to the fields the
 * game UI actually consumes. Mirrors the `premium` shape used by the
 * Stripe flow so both paths feed the same downstream state.
 */
export interface ProStatus {
  active: boolean;
  /** Expiration in epoch ms; `null` when inactive or lifetime. */
  expirationMillis: number | null;
  willRenew: boolean;
  /** Deep link to the store's subscription management page, if any. */
  managementURL: string | null;
}

export type PaywallOutcome =
  | 'purchased'
  | 'restored'
  | 'cancelled'
  | 'not_presented'
  | 'error';

/**
 * Resolve the public SDK key for the current platform. RevenueCat
 * issues one key per store (appl_… / goog_…) plus test-store keys
 * (test_…); we allow per-platform overrides and fall back to the
 * shared key so a single-key setup (e.g. the test store) also works.
 */
function resolveApiKey(): string | null {
  const shared = import.meta.env.VITE_REVENUECAT_API_KEY;
  const ios = import.meta.env.VITE_REVENUECAT_API_KEY_IOS;
  const android = import.meta.env.VITE_REVENUECAT_API_KEY_ANDROID;
  const key = (isCapacitorIOS() ? ios : android) || shared;
  return key && key.length > 0 ? key : null;
}

/**
 * Native billing is *supported* when we're inside the Capacitor shell
 * and an SDK key is baked into the bundle. This is the check UI code
 * uses to decide between the RevenueCat flow and the legacy fallback.
 */
export function isNativeBillingSupported(): boolean {
  return isCapacitorNative() && resolveApiKey() !== null;
}

let configurePromise: Promise<boolean> | null = null;

/**
 * Configure the SDK. Safe to call multiple times — the first call wins
 * and later callers await the same promise. Call as early as possible
 * in app boot (RevenueCat best practice) so the SDK can process any
 * pending store transactions (e.g. Ask-to-Buy approvals, offer-code
 * redemptions) even before the player signs in.
 *
 * Resolves `false` (never rejects) when unsupported or misconfigured.
 */
export function initRevenueCat(): Promise<boolean> {
  if (configurePromise) return configurePromise;
  configurePromise = (async () => {
    if (!isCapacitorNative()) return false;
    const apiKey = resolveApiKey();
    if (!apiKey) {
      console.warn('[revenuecat] no API key configured — native billing disabled');
      return false;
    }
    try {
      // Verbose logs in dev builds only; keeps release logcat clean.
      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN,
      });
      // No appUserID here: the SDK starts with an anonymous user and we
      // alias it to the game account via logIn() after auth. That way
      // purchases made before signing in are preserved.
      await Purchases.configure({ apiKey });
      return true;
    } catch (err) {
      console.error('[revenuecat] configure failed', err);
      return false;
    }
  })();
  return configurePromise;
}

/** Await configuration; `false` when billing is unavailable. */
async function ready(): Promise<boolean> {
  return initRevenueCat();
}

/**
 * Tie the RevenueCat customer to the signed-in game account so the
 * entitlement follows the account across devices/platforms. Call after
 * every successful auth. Returns the fresh customer info (or `null`
 * when billing is unavailable / the call failed).
 */
export async function identifyRevenueCatUser(appUserID: string): Promise<CustomerInfo | null> {
  if (!(await ready())) return null;
  try {
    const { customerInfo } = await Purchases.logIn({ appUserID });
    return customerInfo;
  } catch (err) {
    console.error('[revenuecat] logIn failed', err);
    return null;
  }
}

/**
 * Reset to an anonymous customer on sign-out so the next account on
 * this device doesn't inherit the previous player's entitlements.
 */
export async function resetRevenueCatUser(): Promise<void> {
  if (!(await ready())) return;
  try {
    await Purchases.logOut();
  } catch (err) {
    // LOG_OUT_ANONYMOUS_USER_ERROR just means nobody was logged in.
    if (errorCode(err) !== PURCHASES_ERROR_CODE.LOG_OUT_ANONYMOUS_USER_ERROR) {
      console.error('[revenuecat] logOut failed', err);
    }
  }
}

/** Reduce a `CustomerInfo` to the Pro entitlement snapshot. */
export function proStatusFrom(info: CustomerInfo): ProStatus {
  const ent: PurchasesEntitlementInfo | undefined =
    info.entitlements.active[PRO_ENTITLEMENT_ID];
  return {
    active: !!ent?.isActive,
    expirationMillis: ent?.expirationDateMillis ?? null,
    willRenew: !!ent?.willRenew,
    managementURL: info.managementURL,
  };
}

/**
 * Fetch the current customer info and reduce it to the Pro
 * entitlement. The SDK answers from its local cache first, so this is
 * cheap to call from UI code (e.g. on every upgrade-modal open).
 */
export async function getProStatus(): Promise<ProStatus | null> {
  if (!(await ready())) return null;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return proStatusFrom(customerInfo);
  } catch (err) {
    console.error('[revenuecat] getCustomerInfo failed', err);
    return null;
  }
}

/**
 * The offering currently configured as "current" in the RevenueCat
 * dashboard — its packages (monthly / yearly) are what the paywall
 * renders. Exposed for custom-UI use; the default flow uses
 * {@link presentProPaywall} which fetches this internally.
 */
export async function getCurrentOffering(): Promise<PurchasesOffering | null> {
  if (!(await ready())) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings.current;
  } catch (err) {
    console.error('[revenuecat] getOfferings failed', err);
    return null;
  }
}

export interface PurchaseOutcome {
  outcome: 'purchased' | 'cancelled' | 'error';
  status: ProStatus | null;
}

/**
 * Programmatic purchase of a package from {@link getCurrentOffering}.
 * Used by custom purchase UIs; the standard flow goes through the
 * RevenueCat paywall instead. Cancellation is a normal outcome, not an
 * error.
 */
export async function purchaseProPackage(pkg: PurchasesPackage): Promise<PurchaseOutcome> {
  if (!(await ready())) return { outcome: 'error', status: null };
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    return { outcome: 'purchased', status: proStatusFrom(customerInfo) };
  } catch (err) {
    if (errorCode(err) === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      return { outcome: 'cancelled', status: null };
    }
    console.error('[revenuecat] purchase failed', err);
    return { outcome: 'error', status: null };
  }
}

/**
 * Restore previous store purchases (required by App Store review).
 * Returns the refreshed Pro status, or `null` on failure.
 */
export async function restoreProPurchases(): Promise<ProStatus | null> {
  if (!(await ready())) return null;
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return proStatusFrom(customerInfo);
  } catch (err) {
    console.error('[revenuecat] restore failed', err);
    return null;
  }
}

/**
 * Present the RevenueCat remote paywall (configured in the dashboard,
 * updatable without an app release). By default it is gated on the Pro
 * entitlement — already-subscribed players don't see it.
 */
export async function presentProPaywall(
  opts: { onlyIfNeeded?: boolean } = {},
): Promise<PaywallOutcome> {
  if (!(await ready())) return 'error';
  const onlyIfNeeded = opts.onlyIfNeeded ?? true;
  try {
    const { result } = onlyIfNeeded
      ? await RevenueCatUI.presentPaywallIfNeeded({
          requiredEntitlementIdentifier: PRO_ENTITLEMENT_ID,
        })
      : await RevenueCatUI.presentPaywall();
    switch (result) {
      case PAYWALL_RESULT.PURCHASED: return 'purchased';
      case PAYWALL_RESULT.RESTORED: return 'restored';
      case PAYWALL_RESULT.CANCELLED: return 'cancelled';
      case PAYWALL_RESULT.NOT_PRESENTED: return 'not_presented';
      default: return 'error';
    }
  } catch (err) {
    console.error('[revenuecat] paywall failed', err);
    return 'error';
  }
}

/**
 * Present the RevenueCat Customer Center — self-serve subscription
 * management (cancel, refund requests, plan changes) without leaving
 * the app. Returns `false` when unavailable so callers can fall back
 * to `managementURL` / the Stripe portal.
 */
export async function presentCustomerCenter(): Promise<boolean> {
  if (!(await ready())) return false;
  try {
    await RevenueCatUI.presentCustomerCenter();
    return true;
  } catch (err) {
    console.error('[revenuecat] customer center failed', err);
    return false;
  }
}

/**
 * Subscribe to customer-info pushes from the SDK. Fires after
 * purchases, restores, renewals, and cache refreshes — this is how the
 * game reacts to a subscription bought moments ago on the paywall (or
 * expired mid-session) without polling.
 */
export async function onProStatusChanged(cb: (status: ProStatus) => void): Promise<void> {
  if (!(await ready())) return;
  try {
    await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
      cb(proStatusFrom(customerInfo));
    });
  } catch (err) {
    console.error('[revenuecat] listener registration failed', err);
  }
}

/** Extract the RevenueCat error code from a rejected plugin promise. */
function errorCode(err: unknown): string | null {
  const e = err as { code?: unknown } | null;
  return e && typeof e.code === 'string' ? e.code : null;
}
